// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useEffect, useRef } from 'react'
import styles from '../../Canvas.module.css'
import type { ContextMenuItem, MenuAnchor } from './types'

interface ContextMenuProps {
    open: boolean
    anchor: MenuAnchor | null
    onClose: () => void
    items: ContextMenuItem[]
}

export function ContextMenu(props: ContextMenuProps): ReactElement | null {
    const { open, anchor, onClose, items } = props
    const menuRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node | null
            if (menuRef.current && target && menuRef.current.contains(target)) return
            onClose()
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('pointerdown', handlePointerDown, { capture: true })
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, { capture: true } as any)
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open, onClose])

    if (!open || !anchor) return null

    return (
        <div
            ref={menuRef}
            className={styles.contextMenu}
            style={{ left: anchor.x, top: anchor.y }}
            role="menu"
            data-canvas-ui="true"
            onContextMenu={(e) => e.preventDefault()}
        >
            {items.map((it) => (
                <button
                    key={it.id}
                    className={styles.contextMenuItem}
                    onClick={async () => {
                        try { await it.onClick() } finally { onClose() }
                    }}
                    role="menuitem"
                >
                    {it.icon ? <span className={styles.contextMenuIcon} aria-hidden="true">{it.icon}</span> : null}
                    <span className={styles.contextMenuLabel}>{it.label}</span>
                </button>
            ))}
        </div>
    )
}

export default ContextMenu

